import { useEffect, useRef, useState } from 'react';
import { liveQuery } from 'dexie';
import { api } from '../../api/client.js';
import { db } from '../../offline/db.js';
import { subscribePhotos } from '../../offline/photos.js';
import { outbox } from '../../offline/index.js';

// A job's photos as this phone knows them: the server's (the list is kept on
// the phone for no signal) plus any still waiting to upload. `latest[kind]`
// is the newest of each kind, with a `url` to show it (none for a server
// photo while there's no signal). Meter photos (one per meter) all get one.
export function useJobPhotos(bookingId) {
  const key = `nu.jobPhotos.${bookingId}`;
  const [server, setServer] = useState(() => {
    try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; }
  });
  const [pending, setPending] = useState([]);
  const [urls, setUrls] = useState({});
  // Removed on this phone, still to reach the server.
  const [gone, setGone] = useState(() => new Set());
  useEffect(() => {
    const sub = liveQuery(() => db.outbox.where('status').equals('pending').toArray()).subscribe({
      next: (items) => setGone(new Set(items.filter((m) => m.type === 'job.photo.remove' && m.payload?.bookingId === bookingId).map((m) => m.payload.photoId))),
    });
    return () => sub.unsubscribe();
  }, [bookingId]);

  // The server's list, again whenever one of this phone's photos uploads.
  useEffect(() => {
    let live = true;
    const load = () => api(`/my/jobs/${bookingId}/photos`).then((list) => {
      if (!live) return;
      setServer(list);
      try { localStorage.setItem(key, JSON.stringify(list)); } catch { /* fine */ }
    }, () => {});
    load();
    const off = subscribePhotos(load);
    const offOutbox = outbox.subscribe(load);
    return () => { live = false; off(); offOutbox(); };
  }, [bookingId, key]);

  // Photos still on the phone.
  useEffect(() => {
    const sub = liveQuery(() => db.photos.where('bookingId').equals(bookingId).toArray()).subscribe({ next: setPending });
    return () => sub.unsubscribe();
  }, [bookingId]);

  const all = [
    ...server.filter((p) => !gone.has(p.id)).map((p) => ({ ...p, pending: false })),
    ...pending.map((p) => ({ id: p.id, kind: p.kind, takenAt: p.takenAt, blob: p.blob, pending: true })),
  ].sort((a, b) => String(a.takenAt).localeCompare(String(b.takenAt)));
  const latest = {};
  for (const p of all) latest[p.kind] = p;

  // Thumbnails for the newest of each kind, and every meter photo. Each is
  // made once and kept while the job is open, so a photo that has just
  // uploaded keeps the thumbnail from the phone's copy instead of downloading
  // it again. One that can't be downloaded (weak signal) is tried again: in
  // 20 seconds, and when the signal comes back.
  const wanted = [...new Set([...Object.values(latest), ...all.filter((p) => p.kind === 'meter')])];
  const missing = wanted.filter((p) => !urls[p.id]);
  const missingIds = missing.map((p) => p.id).join(',');
  const [tries, setTries] = useState(0);
  const made = useRef({});
  useEffect(() => () => {
    Object.values(made.current).forEach((u) => URL.revokeObjectURL(u));
    made.current = {};
    setUrls({});
  }, [bookingId]);
  useEffect(() => {
    if (!missing.length) return undefined;
    let live = true;
    let again = null;
    (async () => {
      let failed = false;
      for (const p of missing) {
        const blob = p.blob || await api(`/my/jobs/${bookingId}/photos/${p.id}`, { as: 'blob' }).catch(() => null);
        if (!live) return;
        if (!blob) { failed = true; continue; }
        if (made.current[p.id]) continue;
        const url = URL.createObjectURL(blob);
        made.current[p.id] = url;
        setUrls((u) => ({ ...u, [p.id]: url }));
      }
      if (failed && live) again = setTimeout(() => setTries((n) => n + 1), 20_000);
    })();
    const online = () => setTries((n) => n + 1);
    window.addEventListener('online', online);
    return () => { live = false; clearTimeout(again); window.removeEventListener('online', online); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, missingIds, tries]);

  for (const k of Object.keys(latest)) latest[k] = { ...latest[k], url: urls[latest[k].id] || null };
  return { latest, all: all.map((p) => ({ ...p, url: urls[p.id] || null })) };
}
